/**
 * Tool «Mastepunkt langs linje»: select mastpoints along a shared line, as input for a calculation.
 *
 * The first click may be any mastpoint (or any part of it: pole, crossbeam, support, utility link).
 * After every change of the selection, `POST Section/masts/selection` returns the neighbouring masts
 * that can be selected next, as branches with the closest mast first. Those masts get a marker
 * (a 72 px target a little below the top of the mast, so it is easy to hit at any zoom) and are the
 * only ones that can be clicked. Clicking the mast at position 4 of a branch selects positions 0–3 as
 * well; for a nested branch the parent branch's masts are selected first, because a nested branch
 * forks off after the last mast of its parent.
 *
 * Branches are cached across clicks: a response only covers the direction the user went, so a
 * cached branch is kept until a response returns a branch that shares one of its masts.
 * 
 * Selected masts are joined by an animated line (green dashes flowing over white), from the mast a
 * branch starts at through each mast in it. Like the markers, it is drawn in the overlay.
 *
 * Markers have three states: available (cyan with a plus), hovered (amber with a glow, shown on every
 * mast a click would select) and selected (green with a check). Selected masts keep their marker as
 * the selection grows, after they stop being available neighbours. Markers are HTML elements laid
 * over the canvas and moved after every rendered frame (`placeMarkers`), not Cesium entities:
 * showing and hiding Cesium billboards while the user hovers crashed the render loop.
 *
 * Hover and clicks on markers are resolved in screen space (`hitboxAt`), not with `scene.pick`: the
 * pointer is tested against each marker's projected point. That costs no render pass or GPU readback
 * per mouse move and guarantees that the markers shown as hovered are the ones a click selects.
 * `scene.pick` is only used for the first click (any part of any mast).
 *
 * Ctrl + left click on a selected mast removes it and every mast after it. Esc cancels the tool and
 * clears the selection. Right click finishes the path (at least 2 masts): the mast ids are handed
 * to path-mode (`pathMode.ts`), which fetches `Section/calculate/info` and owns everything after.
 *
 * The store is only updated once the backend has accepted a change, so the UI never shows a mast as
 * selected that the backend does not know about.
 *
 * Template: `pointToGroundTool.ts` (module-level state, toggle called twice per session).
 */
import { get } from 'svelte/store';
import {
	Cartesian2,
	Cartesian3,
	defined,
	KeyboardEventModifier,
	SceneTransforms,
	ScreenSpaceEventType,
	type Camera,
	type Entity
} from 'cesium';
import { toast } from '$lib/components/organisms/toast/toast';
import type { IMastBranch } from '$lib/models/IMastSelectionModel';
import type { IMastPointMinimalModel } from '$lib/models/IMastPointMinimalModel';
import { MastSelectionService } from '$lib/services/mast-selection-service';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { useDataSources } from '../cesium-datasources';
import { busyStateMessage, useCesiumViewer } from '../cesium-viewer';
import { disableDistanceAnalysisMode } from './distanceAnalysisMode';
import { areaModeState } from './area-mode-state.svelte';
import {
	disableDefaultLeftClickHandler,
	removeAllScreenSpaceInputActions,
	toggleEscapeToolListener
} from '../map-tools';
import { mapToolsStore } from '../map-tools-store';
import {
	entityIsPartOfMastpoint,
	getEntityPropertyBag,
	type PropertyBagEntityTypes
} from '../map-utils';
import { collectGroupEntities, getGroupRoot } from './utils';

/** Masts per branch requested from the backend (`branchLengthN`). */
const BRANCH_LENGTH_N = 20;
/** Right click needs at least this many selected masts to finish the path. */
export const MIN_MASTS_TO_FINISH = 2;
/** The marker's clickable area is a circle this many pixels across at any zoom. */
const MARKER_PX = 72;
/** The circle behind the glyph, in pixels across. */
const MARKER_CIRCLE_PX = 28;
/** How long a marker takes to change colour and fade its glow in or out. */
const MARKER_ANIMATION_MS = 200;
/** The colour of each marker status. The area, its dashed edge and the glow are drawn in it. */
const MARKER_COLOR: Record<MarkerStatus, string> = {
	available: '#1FADEF',
	hovered: '#f59e0b',
	selected: '#10b981'
};
/** The marker sits this far below the top of the mast. */
const MARKER_BELOW_TOP_M = 1;
/** Used for a mast without poles, whose position is taken as its foot. */
const FALLBACK_POLE_LENGTH = 10;
/** The line between selected masts: green dashes on white, flowing from the earlier mast to the later. */
const LINE_COLOR = MARKER_COLOR.selected;
const LINE_WIDTH_PX = 5;
const LINE_DASH_PX = 14;
const LINE_GAP_PX = 10;
/** How long the dashes take to move one dash and gap along the line. Lower is faster. */
const LINE_FLOW_MS = 600;

/**
 * One run of masts from a response, closest first, flattened out of the response's tree. `prefix` is
 * the masts to select before the first of them: the mast the response was requested for and, for a
 * nested branch, every mast of the branches it forks off from.
 */
interface ICachedBranch {
	prefix: number[];
	mastIds: number[];
}

/** Where a mast is marked. */
interface IMastGeometry {
	/** Top of the tallest pole. */
	top: Cartesian3;
	/** On the mast, `MARKER_BELOW_TOP_M` below the top. The marker is drawn here. */
	marker: Cartesian3;
}

/** A drawn marker: an element in the marker layer, placed over its mast on the screen. */
interface IHitbox {
	element: HTMLDivElement;
	position: Cartesian3;
	/** The status shown, or `undefined` when the mast is neither selected nor available. */
	status: MarkerStatus | undefined;
}

const state: {
	/**
	 * Branches from every response so far. A response only describes the masts beyond the clicked
	 * one, so branches in other directions are kept until a response returns one that shares a mast
	 * with them. Nested branches are kept as entries of their own, so a fork survives when only the
	 * branch it forks from is replaced.
	 */
	branches: ICachedBranch[];
	/**
	 * Mast id → the masts to select when it is clicked: the mast its branch starts at, everything
	 * before it in its branch, itself last.
	 */
	available: Map<number, number[]>;
	/** Drawn markers per mast id: the selected masts, and the available ones whose position is known. */
	hitboxes: Map<number, IHitbox>;
	/** The hitbox under the pointer. */
	hovered: number | undefined;
	/** Last pointer position over the map, to restore the hover after the hitboxes are redrawn. */
	pointer: Cartesian2 | undefined;
	/** Pairs of selected masts joined by a line. */
	links: [number, number][];
	/** Locks clicks while a request is in flight. */
	busy: boolean;
	/** Bumped on cleanup, so a response that arrives after the tool was turned off is dropped. */
	session: number;
	/** Whether the global distance view was on when the tool started. Carried into path-mode. */
	distanceModeWasActive: boolean;
} = {
	branches: [],
	available: new Map(),
	hitboxes: new Map(),
	hovered: undefined,
	pointer: undefined,
	links: [],
	busy: false,
	session: 0,
	distanceModeWasActive: false
};

const selectedIds = () => get(mapToolsStore).mastpointsAlongLine.mastpointIds;

function mastpointIdOf(root: Entity): number | undefined {
	const id = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(root)?.data?.id;
	return typeof id === 'number' ? id : undefined;
}

/** The mastpoint root entity for a picked entity, or `undefined` when it is not part of a mastpoint. */
function pickedMastpointRoot(entity: Entity): Entity | undefined {
	const isMastpointPart =
		entity.id?.startsWith(ENTITY_ID_PREFIX.MASTPOINT) ||
		entityIsPartOfMastpoint(getEntityPropertyBag<PropertyBagEntityTypes>(entity));
	if (!isMastpointPart) return undefined;
	const root = getGroupRoot(entity);
	return root?.id?.startsWith(ENTITY_ID_PREFIX.MASTPOINT) ? root : undefined;
}

/** The id of the mast under the pointer, picked from the rendered scene. Hitboxes are not masts. */
function pickMastpointId(position: Cartesian2): number | undefined {
	const picked = useCesiumViewer().scene.pick(position);
	const entity = defined(picked) ? (picked.id as Entity | undefined) : undefined;
	if (!entity?.id) return undefined;
	const root = pickedMastpointRoot(entity);
	return root ? mastpointIdOf(root) : undefined;
}

function findMastpointEntity(mastpointId: number): Entity | undefined {
	const { mapdataDataSource } = useDataSources();
	const viewer = useCesiumViewer();
	const id = ENTITY_ID_PREFIX.MASTPOINT + mastpointId;
	const inMapdata = mapdataDataSource.entities.getById(id);
	if (inMapdata) return inMapdata;
	for (let i = 0; i < viewer.dataSources.length; i++) {
		const found = viewer.dataSources.get(i).entities.getById(id);
		if (found) return found;
	}
	return undefined;
}

function poleEntities(root: Entity): Entity[] {
	return collectGroupEntities(root, root.entityCollection).filter((e) =>
		e.id.startsWith(ENTITY_ID_PREFIX.POLE)
	);
}

/**
 * Where a mast is marked, from its pole cylinders (a Cesium cylinder is positioned at its middle).
 * `top` is the top of the tallest pole. `marker` is
 * `MARKER_BELOW_TOP_M` below it, on the mast, so the marker is above the ground at any pole height.
 */
function mastGeometry(mastpointId: number): IMastGeometry | undefined {
	const root = findMastpointEntity(mastpointId);
	if (!root) return undefined;
	const time = useCesiumViewer().clock.currentTime;

	const poles = poleEntities(root).flatMap((pole) => {
		const position = pole.position?.getValue(time) as Cartesian3 | undefined;
		const length = pole.cylinder?.length?.getValue(time) as number | undefined;
		return position && length ? [{ position, length }] : [];
	});

	/** `point` moved straight up (along the ellipsoid normal) by `meters`, negative to move down. */
	const raise = (point: Cartesian3, meters: number) =>
		Cartesian3.add(
			point,
			Cartesian3.multiplyByScalar(Cartesian3.normalize(point, new Cartesian3()), meters, new Cartesian3()),
			new Cartesian3()
		);

	if (poles.length === 0) {
		const foot = root.position?.getValue(time) as Cartesian3 | undefined;
		if (!foot) return undefined;
		const top = raise(foot, FALLBACK_POLE_LENGTH);
		return { top, marker: raise(top, -MARKER_BELOW_TOP_M) };
	}

	const center = Cartesian3.multiplyByScalar(
		poles.reduce((sum, p) => Cartesian3.add(sum, p.position, sum), new Cartesian3()),
		1 / poles.length,
		new Cartesian3()
	);
	const tallest = Math.max(...poles.map((p) => p.length));
	const top = raise(center, tallest / 2);
	// A pole shorter than twice the offset gets its marker at the middle instead.
	return { top, marker: raise(top, -Math.min(MARKER_BELOW_TOP_M, tallest / 2)) };
}

/**
 * Flattens a response's tree into one entry per branch. A nested branch forks off after the last mast
 * of its parent, so its `prefix` is the parent's prefix followed by the parent's masts. Every entry
 * is then complete on its own: it can stay in the cache when the branches around it are replaced.
 */
function flattenBranches(branches: IMastBranch[], prefix: number[]): ICachedBranch[] {
	return branches.flatMap((branch) => [
		{ prefix, mastIds: branch.mastIds },
		...flattenBranches(branch.branches ?? [], [...prefix, ...branch.mastIds])
	]);
}

/**
 * Keeps the cached branches, except those that share a mast with a branch in the response: the
 * response is newer, so it replaces them. Response branches are added after the kept ones. Only the
 * masts of a branch itself count, not those of branches that fork off it, so a fork survives when the
 * branch it forks from is replaced.
 */
function mergeBranches(cached: ICachedBranch[], incoming: ICachedBranch[]): ICachedBranch[] {
	const incomingIds = new Set(incoming.flatMap(({ mastIds }) => mastIds));
	const kept = cached.filter(({ mastIds }) => !mastIds.some((mastId) => incomingIds.has(mastId)));
	return [...kept, ...incoming];
}

/** Fills `state.available` from the branches, skipping masts that are already selected. */
function collectAvailable(branches: ICachedBranch[], selected: Set<number>) {
	for (const { prefix, mastIds } of branches) {
		mastIds.forEach((mastId, index) => {
			if (selected.has(mastId) || state.available.has(mastId)) return;
			state.available.set(mastId, [...prefix, ...mastIds.slice(0, index + 1)]);
		});
	}
}

function clearHitboxes() {
	removeMarkerLayer();
	state.hitboxes.clear();
	state.hovered = undefined;
}

/** The layer the markers are placed in, once it exists. */
let markerLayerElement: HTMLDivElement | undefined;
/** The SVG in the marker layer that the lines are drawn in, below the markers. */
let lineLayerElement: SVGSVGElement | undefined;

/** A drawn line between two selected masts: an SVG group placed between their markers on the screen. */
interface IDrawnLine {
	element: SVGGElement;
	start: Cartesian3;
	end: Cartesian3;
}
let drawnLines: IDrawnLine[] = [];

const SVG_NS = 'http://www.w3.org/2000/svg';

type MarkerStatus = 'available' | 'hovered' | 'selected';

/**
 * The marker styles. A marker is three layers: the area (the clickable circle, a radial gradient in
 * the status colour that fades towards a dashed edge), the circle (status colour, white border) and
 * the glyph on it (white: a plus when the mast can be added, a check once it is selected). A hovered
 * marker gets a glow around the circle, inside the area.
 *
 * The status colour is a registered custom property (`@property`), so the browser animates it, and
 * the gradient, edge and circle drawn from it change colour together over `MARKER_ANIMATION_MS`.
 */
const MARKER_CSS = `
@property --mpal-color {
	syntax: '<color>';
	inherits: true;
	initial-value: ${MARKER_COLOR.available};
}
.mpal-marker {
	position: absolute;
	left: 0;
	top: 0;
	width: ${MARKER_PX}px;
	height: ${MARKER_PX}px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	border: 2px dashed color-mix(in srgb, var(--mpal-color) 55%, white);
	background: radial-gradient(
		circle closest-side,
		color-mix(in srgb, var(--mpal-color) 60%, transparent),
		color-mix(in srgb, var(--mpal-color) 35%, transparent) 75%,
		color-mix(in srgb, var(--mpal-color) 15%, transparent)
	);
	transition: --mpal-color ${MARKER_ANIMATION_MS}ms ease;
}
.mpal-marker[data-status='available'] { --mpal-color: ${MARKER_COLOR.available}; }
.mpal-marker[data-status='hovered'] { --mpal-color: ${MARKER_COLOR.hovered}; }
.mpal-marker[data-status='selected'] { --mpal-color: ${MARKER_COLOR.selected}; }
/* A selected mast shows only its circle and check: the area and dashed edge are for masts to pick. */
.mpal-marker[data-status='selected'] {
	border-color: transparent;
	background: none;
}
.mpal-circle {
	width: ${MARKER_CIRCLE_PX}px;
	height: ${MARKER_CIRCLE_PX}px;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	border: 2px solid #ffffff;
	background: var(--mpal-color);
	color: #ffffff;
	box-shadow: 0 0 0 0 transparent;
	transition: box-shadow ${MARKER_ANIMATION_MS}ms ease;
}
.mpal-marker[data-status='hovered'] .mpal-circle {
	box-shadow: 0 0 12px 6px color-mix(in srgb, var(--mpal-color) 75%, transparent);
}
.mpal-marker[data-status='selected'] .mpal-plus,
.mpal-marker:not([data-status='selected']) .mpal-check {
	display: none;
}
.mpal-lines {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	overflow: visible;
}
.mpal-lines line {
	stroke-width: ${LINE_WIDTH_PX}px;
	stroke-linecap: butt;
}
.mpal-lines .mpal-line-shadow {
	stroke: rgba(0, 0, 0, 0.35);
	stroke-width: ${LINE_WIDTH_PX + 2}px;
}
.mpal-lines .mpal-line-base {
	stroke: #ffffff;
}
/* Moving the dash offset from one period down to zero moves the dashes towards the line's end. */
.mpal-lines .mpal-line-flow {
	stroke: ${LINE_COLOR};
	stroke-dasharray: ${LINE_DASH_PX} ${LINE_GAP_PX};
	animation: mpal-flow ${LINE_FLOW_MS}ms linear infinite;
}
@keyframes mpal-flow {
	from { stroke-dashoffset: ${LINE_DASH_PX + LINE_GAP_PX}; }
	to { stroke-dashoffset: 0; }
}
`;

/** The circle and both glyphs; CSS shows the glyph that fits the status. */
const MARKER_HTML = `
<div class="mpal-circle">
	<svg class="mpal-plus" width="14" height="14" viewBox="-7 -7 14 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
		<path d="M-4.5 0H4.5M0 -4.5V4.5" />
	</svg>
	<svg class="mpal-check" width="14" height="14" viewBox="-7 -7 14 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
		<path d="M-5 0.5L-1.5 4L5 -3.5" />
	</svg>
</div>`;

/** Shows the marker in `status`; `undefined` hides it. */
function setMarkerStatus(hitbox: IHitbox, status: MarkerStatus | undefined) {
	if (hitbox.status === status) return;
	hitbox.status = status;
	hitbox.element.style.display = status === undefined ? 'none' : '';
	if (status !== undefined) hitbox.element.dataset.status = status;
}

/** The element all markers are placed in, laid over the map canvas. Added on first use. */
function markerLayer(): HTMLDivElement {
	if (markerLayerElement) return markerLayerElement;
	const viewer = useCesiumViewer();
	const layer = document.createElement('div');
	// Markers never take pointer events: hover and clicks go to the canvas and are resolved in
	// `hitboxAt`, so the Cesium handlers (including right click) work over a marker.
	layer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none;';
	const style = document.createElement('style');
	style.textContent = MARKER_CSS;
	layer.appendChild(style);
	// First in the layer, so the markers are drawn on top of the line ends.
	const lines = document.createElementNS(SVG_NS, 'svg');
	lines.setAttribute('class', 'mpal-lines');
	layer.appendChild(lines);
	lineLayerElement = lines;
	viewer.container.appendChild(layer);
	viewer.scene.postRender.addEventListener(placeMarkers);
	markerLayerElement = layer;
	return layer;
}

function removeMarkerLayer() {
	if (!markerLayerElement) return;
	useCesiumViewer().scene.postRender.removeEventListener(placeMarkers);
	markerLayerElement.remove();
	markerLayerElement = undefined;
	lineLayerElement = undefined;
	drawnLines = [];
}

const scratchLineStart = new Cartesian2();
const scratchLineEnd = new Cartesian2();

/**
 * Moves every shown marker, and every line, to where its masts are on the screen. Runs after each
 * rendered frame, so the overlay follows the camera in step with the map. A marker behind the camera
 * is hidden, and so is a line with an end behind it.
 */
function placeMarkers() {
	const { scene, camera } = useCesiumViewer();
	for (const line of drawnLines) {
		const inFront = isInFrontOfCamera(camera, line.start) && isInFrontOfCamera(camera, line.end);
		const start = inFront
			? SceneTransforms.worldToWindowCoordinates(scene, line.start, scratchLineStart)
			: undefined;
		const end = inFront
			? SceneTransforms.worldToWindowCoordinates(scene, line.end, scratchLineEnd)
			: undefined;
		line.element.style.visibility = start && end ? 'visible' : 'hidden';
		if (!start || !end) continue;
		for (const segment of line.element.children) {
			segment.setAttribute('x1', start.x.toFixed(1));
			segment.setAttribute('y1', start.y.toFixed(1));
			segment.setAttribute('x2', end.x.toFixed(1));
			segment.setAttribute('y2', end.y.toFixed(1));
		}
	}
	for (const hitbox of state.hitboxes.values()) {
		if (hitbox.status === undefined) continue;
		const window = isInFrontOfCamera(camera, hitbox.position)
			? SceneTransforms.worldToWindowCoordinates(scene, hitbox.position, scratchWindow)
			: undefined;
		hitbox.element.style.visibility = window ? 'visible' : 'hidden';
		if (window) {
			hitbox.element.style.transform = `translate(${window.x - MARKER_PX / 2}px, ${window.y - MARKER_PX / 2}px)`;
		}
	}
}

/**
 * Adds a mast's marker to the marker layer. A marker is created once and kept until the tool ends,
 * so a change of status only changes its `data-status`, which the CSS animates.
 */
function createHitbox(mastId: number): IHitbox | undefined {
	const position = mastGeometry(mastId)?.marker;
	if (!position) return undefined;
	const element = document.createElement('div');
	element.className = 'mpal-marker';
	element.innerHTML = MARKER_HTML;
	element.style.display = 'none';
	markerLayer().appendChild(element);
	const hitbox: IHitbox = { element, position, status: undefined };
	state.hitboxes.set(mastId, hitbox);
	return hitbox;
}

/**
 * Gives every selected mast and every available mast its marker, and hides the markers of masts that
 * are neither. Selected masts keep their marker after they stop being available. Then restores the
 * hover for the last pointer position.
 */
function drawHitboxes(selected: number[]) {
	const wanted = new Map<number, MarkerStatus>();
	for (const mastId of state.available.keys()) wanted.set(mastId, 'available');
	for (const mastId of selected) wanted.set(mastId, 'selected');

	// Every marker gets a fresh status below, so nothing is hovered until the pointer is tested again.
	state.hovered = undefined;
	for (const [mastId, hitbox] of state.hitboxes) {
		if (!wanted.has(mastId)) setMarkerStatus(hitbox, undefined);
	}
	for (const [mastId, status] of wanted) {
		const hitbox = state.hitboxes.get(mastId) ?? createHitbox(mastId);
		if (hitbox) setMarkerStatus(hitbox, status);
	}
	placeMarkers();
	if (state.pointer) setHovered(hitboxAt(state.pointer));
}

const scratchToPoint = new Cartesian3();
const scratchWindow = new Cartesian2();

/** `worldToWindowCoordinates` mirrors points behind the camera onto the screen, so those are skipped. */
function isInFrontOfCamera(camera: Camera, point: Cartesian3): boolean {
	const toPoint = Cartesian3.subtract(point, camera.positionWC, scratchToPoint);
	return Cartesian3.dot(toPoint, camera.directionWC) > 0;
}

/**
 * The mast id of the marker under a window position, or `undefined`. A marker is a circle of
 * `MARKER_PX` around its point on the screen, so the test is the distance in pixels to that point;
 * the closest marker wins. By default only available masts are tested (the ones a click selects);
 * with `selected` set, only selected masts are (the ones Ctrl + click removes).
 */
function hitboxAt(position: Cartesian2, selected = false): number | undefined {
	const { scene, camera } = useCesiumViewer();
	let closestId: number | undefined;
	let closestDistance = MARKER_PX / 2;
	for (const [mastId, hitbox] of state.hitboxes) {
		if (hitbox.status === undefined || (hitbox.status === 'selected') !== selected) continue;
		if (!isInFrontOfCamera(camera, hitbox.position)) continue;
		const window = SceneTransforms.worldToWindowCoordinates(scene, hitbox.position, scratchWindow);
		if (!window) continue;
		const distance = Cartesian2.distance(position, window);
		if (distance <= closestDistance) {
			closestId = mastId;
			closestDistance = distance;
		}
	}
	return closestId;
}

/** The markers to show as hovered when `mastId` is hovered: every mast a click on it would select. */
function hoverTargets(mastId: number | undefined): IHitbox[] {
	if (mastId === undefined) return [];
	return (state.available.get(mastId) ?? []).flatMap((id) => {
		const hitbox = state.hitboxes.get(id);
		return hitbox && (hitbox.status === 'available' || hitbox.status === 'hovered') ? [hitbox] : [];
	});
}

function setHovered(mastId: number | undefined) {
	if (mastId === state.hovered) return;
	for (const hitbox of hoverTargets(state.hovered)) setMarkerStatus(hitbox, 'available');
	state.hovered = mastId;
	for (const hitbox of hoverTargets(mastId)) setMarkerStatus(hitbox, 'hovered');
}

function onMouseMove(movement: { endPosition: Cartesian2 }) {
	state.pointer = Cartesian2.clone(movement.endPosition, state.pointer);
	if (state.busy) return;
	setHovered(hitboxAt(movement.endPosition));
}

/** Cesium sends no MOUSE_MOVE outside the canvas, so the hover would stay where the pointer left. */
function onMouseLeave() {
	state.pointer = undefined;
	setHovered(undefined);
}

/**
 * Draws a line between the markers of each linked pair of selected masts, in the SVG under the
 * markers: a shadow and a white base, with green dashes on top that flow from `from` to `to`. The
 * lines are screen-space overlay like the markers, so they show through terrain and buildings.
 */
function drawLines() {
	markerLayer();
	const svg = lineLayerElement;
	if (!svg) return;
	svg.replaceChildren();
	drawnLines = [];
	for (const [from, to] of state.links) {
		const start = mastGeometry(from)?.marker;
		const end = mastGeometry(to)?.marker;
		if (!start || !end) continue;
		const element = document.createElementNS(SVG_NS, 'g');
		for (const className of ['mpal-line-shadow', 'mpal-line-base', 'mpal-line-flow']) {
			const segment = document.createElementNS(SVG_NS, 'line');
			segment.setAttribute('class', className);
			element.appendChild(segment);
		}
		svg.appendChild(element);
		drawnLines.push({ element, start, end });
	}
	placeMarkers();
}

/**
 * Locks clicks and shows the map's blocking overlay (`busyStateMessage`) while a request is in
 * flight. Only clears the overlay when this tool set it, so another tool's message is left alone.
 */
function setBusy(busy: boolean) {
	if (busy) {
		busyStateMessage.set('Oppdaterer seleksjon...');
	} else if (state.busy) {
		busyStateMessage.set('');
	}
	state.busy = busy;
}

/**
 * Asks the backend for the masts available after `newIds`, and on success commits the selection:
 * store, lines and markers. On failure nothing changes. `newLinks` are the lines to add
 * for masts this change selects; lines to masts that are no longer selected are dropped.
 */
async function applySelection(
	newIds: number[],
	selectedMastId: number,
	newLinks: [number, number][] = []
) {
	const session = state.session;
	setBusy(true);
	try {
		const response = await MastSelectionService.getSelection({
			branchLengthN: BRANCH_LENGTH_N,
			selectedMastId,
			selectedMastIds: newIds
		});

		// The tool was turned off while waiting.
		if (session !== state.session) return;


		const selected = new Set(newIds);
		state.links = [
			...state.links.filter(([from, to]) => selected.has(from) && selected.has(to)),
			...newLinks
		];
		drawLines();

		state.branches = mergeBranches(
			state.branches,
			flattenBranches(response.branches ?? [], [selectedMastId])
		);
		state.available.clear();
		collectAvailable(state.branches, selected);
		drawHitboxes(newIds);

		mapToolsStore.update((store) => {
			store.mastpointsAlongLine.mastpointIds = newIds;
			return store;
		});
	} catch (error) {
		if (session !== state.session) return;
		console.error('[mastpointsAlongLine] Section/masts/selection failed', error);
		toast({
			type: 'error',
			title: 'Kunne ikke hente mastepunkt',
			description: 'Utvalget ble ikke endret. Vennligst prøv igjen.'
		});
	} finally {
		if (session === state.session) setBusy(false);
	}
}

function onLeftClick(event: { position: Cartesian2 }) {
	useCesiumViewer().selectedEntity = undefined;
	if (state.busy) return;

	const current = selectedIds();
	if (current.length === 0) {
		const mastpointId = pickMastpointId(event.position);
		if (mastpointId !== undefined) applySelection([mastpointId], mastpointId);
		return;
	}

	// The same test as the hover, so a click selects exactly what is shown as hovered.
	const mastpointId = hitboxAt(event.position);
	const path = mastpointId === undefined ? undefined : state.available.get(mastpointId);
	if (mastpointId === undefined || !path) return;
	// Each mast in the path is joined to the one before it; the path starts at a selected mast.
	const links = path
		.slice(1)
		.map((to, index): [number, number] => [path[index], to])
		.filter(([, to]) => !current.includes(to));
	applySelection([...current, ...path.filter((id) => !current.includes(id))], mastpointId, links);
}

/** Ctrl + click: removes the clicked selected mast and every mast selected after it. */
function onCtrlLeftClick(event: { position: Cartesian2 }) {
	useCesiumViewer().selectedEntity = undefined;
	if (state.busy) return;

	const mastpointId = hitboxAt(event.position, true) ?? pickMastpointId(event.position);
	const current = selectedIds();
	const index = mastpointId === undefined ? -1 : current.indexOf(mastpointId);
	if (index === -1) return;

	const remaining = current.slice(0, index);
	if (remaining.length === 0) {
		resetSelection();
		return;
	}
	applySelection(remaining, remaining[remaining.length - 1]);
}

/** Empties the tool's state and removes lines and markers. Leaves the store alone. */
function clearToolState() {
	state.branches = [];
	state.links = [];
	state.available.clear();
	// Removes the marker layer, and the lines with it.
	clearHitboxes();
}

/** Clears selection, lines and markers, without touching the Cesium input handlers. */
function resetSelection() {
	clearToolState();
	mapToolsStore.update((store) => {
		store.mastpointsAlongLine.mastpointIds = [];
		return store;
	});
}

/**
 * Removes lines, markers, the Ctrl+click handler and the Esc listener without touching
 * `mapToolsStore`. Idempotent. Called from `disableAllTools`/`disableAllToolsExceptToggleToolByName`
 * when another tool or a side menu turns this tool off, and from the off branch of
 * `toggleMastpointsAlongLineTool`.
 */
export function cleanupMastpointsAlongLineTool(): void {
	state.session++;
	setBusy(false);
	clearToolState();
	state.pointer = undefined;
	const viewer = useCesiumViewer();
	viewer.canvas.removeEventListener('mouseleave', onMouseLeave);
	// The plain MOUSE_MOVE handler is removed by `removeAllScreenSpaceInputActions`.
	viewer.screenSpaceEventHandler.removeInputAction(
		ScreenSpaceEventType.LEFT_CLICK,
		KeyboardEventModifier.CTRL
	);
	toggleEscapeToolListener({ toggleOn: false });
}

/**
 * Right click: finishes the path. The selected masts are handed to path-mode, which fetches
 * `Section/calculate/info` for them. Fewer than `MIN_MASTS_TO_FINISH` masts shows a warning and
 * keeps the tool active.
 */
export function finishMastpointsAlongLineTool(): void {
	if (state.busy) return;
	const mastIds = [...selectedIds()];
	if (mastIds.length < MIN_MASTS_TO_FINISH) {
		toast({
			type: 'warning',
			title: 'For få mastepunkt',
			description: `Velg minst ${MIN_MASTS_TO_FINISH} mastepunkt før du avslutter.`
		});
		return;
	}
	const showDistances = state.distanceModeWasActive;
	finishedSelection = { mastIds, links: [...state.links], branches: [...state.branches] };
	// Turns the tool off, which clears markers, lines and the store selection.
	toggleMastpointsAlongLineTool();
	void enterPathMode(mastIds, showDistances);
}

/**
 * The last finished selection's lines and branches, kept after the tool turns off so
 * `resumeMastpointsAlongLineTool` can restore them: neither can be rebuilt from the mast ids alone.
 */
let finishedSelection:
	| { mastIds: number[]; links: [number, number][]; branches: ICachedBranch[] }
	| undefined;

/**
 * Starts the tool with `mastIds` already selected, e.g. to change the path that path-mode shows.
 * When they are the last finished selection, its lines and branches are restored as well, so every
 * direction the user could pick from before is available again. Otherwise the masts are selected
 * without lines, and only the branches from the last mast are offered.
 *
 * Path-mode stays active meanwhile: finishing replaces it, cancelling with Esc returns to it.
 * `showDistances` carries path-mode's "Vis avstander" into the next finish.
 */
export function resumeMastpointsAlongLineTool(mastIds: number[], showDistances = false): void {
	if (mastIds.length === 0) return;
	if (!get(mapToolsStore).mastpointsAlongLine.toolEnabled) toggleMastpointsAlongLineTool();
	state.distanceModeWasActive ||= showDistances;

	const saved =
		finishedSelection &&
		finishedSelection.mastIds.length === mastIds.length &&
		finishedSelection.mastIds.every((id, index) => id === mastIds[index])
			? finishedSelection
			: undefined;
	state.links = saved ? [...saved.links] : [];
	state.branches = saved ? [...saved.branches] : [];
	void applySelection([...mastIds], mastIds[mastIds.length - 1]);
}

/**
 * Dynamic imports on purpose: `pathMode`/`areaMode` import the map-utils chain, which imports
 * map-tools-store, which imports this module. Keeping them out of the static graph leaves the
 * module evaluation order unchanged.
 */
async function enterPathMode(mastIds: number[], showDistances: boolean): Promise<void> {
	const [{ activatePathMode }, { deactivateAreaMode }] = await Promise.all([
		import('./pathMode'),
		import('./areaMode')
	]);
	// Area-mode and path-mode share the right-hand panel slot — never both.
	if (areaModeState.active) deactivateAreaMode();
	activatePathMode({ mastIds, showDistances });
}

/**
 * Turns the tool on or off. Registered as `deactivateTool` in `mapToolsStore.mastpointsAlongLine`,
 * so Esc (`handleKeyPress`) cancels here and clears the selection. Right click finishes instead,
 * see `finishMastpointsAlongLineTool`.
 *
 * A function declaration on purpose: map-tools-store.ts references it at module init, and the two
 * modules import each other. Function declarations are hoisted, so the order is harmless.
 */
export function toggleMastpointsAlongLineTool(): void {
	const viewer = useCesiumViewer();
	const { screenSpaceEventHandler } = viewer;

	removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	mapToolsStore.disableAllToolsExceptToggleToolByName('mastpointsAlongLine', viewer);
	viewer.selectedEntity = undefined;

	if (get(mapToolsStore).mastpointsAlongLine.toolEnabled) {
		toggleEscapeToolListener({ toggleOn: true });
		disableDefaultLeftClickHandler(screenSpaceEventHandler);
		viewer.canvas.addEventListener('mouseleave', onMouseLeave);
		screenSpaceEventHandler.setInputAction(onMouseMove, ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.setInputAction(onLeftClick, ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.setInputAction(
			onCtrlLeftClick,
			ScreenSpaceEventType.LEFT_CLICK,
			KeyboardEventModifier.CTRL
		);
		screenSpaceEventHandler.setInputAction(
			finishMastpointsAlongLineTool,
			ScreenSpaceEventType.RIGHT_CLICK
		);
		// The global distance view and path-mode's per-section distance lines draw the same
		// measurement — never both at once. Turn the global view off as the tool starts,
		// remembering whether it was on so path-mode can re-show the equivalent lines.
		state.distanceModeWasActive = disableDistanceAnalysisMode();
	} else {
		cleanupMastpointsAlongLineTool();
		mapToolsStore.update((store) => {
			store.mastpointsAlongLine.mastpointIds = [];
			return store;
		});
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
		screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
		// Restores Cesium's original LEFT_CLICK (entity selection).
		removeAllScreenSpaceInputActions(viewer, screenSpaceEventHandler);
	}
}
